/**
 * ChannelProfit — reusable ordering-channel profit breakdown (Direct vs Uber Eats
 * vs DoorDash) with realistic AU aggregator commissions, plus an optional
 * "what-if" pickup slider that shows the weekly profit gained by shifting a
 * share of delivery orders to direct pickup. Used network-wide (Delivery tab)
 * and per store (LocationDrawer).
 */
import React, { useState } from 'react';
import { Store, Sparkles, Gift, Check } from 'lucide-react';
import { toast } from 'sonner';
import Sparkline from './Sparkline';
import { applyPayoff } from '@/components/demo/demoBus';

const MARGINS = { direct: 26, ubereats: 11, doordash: 12 };
const COMMISSION = { direct: 0, ubereats: 30, doordash: 28 };
const money = (n) => `$${Math.round(n).toLocaleString()}`;

export default function ChannelProfit({ sales, mixPct, subtitle, showSlider = true, compact = false }) {
  const [shift, setShift] = useState(0); // % of delivery shifted to direct pickup
  const [offerLive, setOfferLive] = useState(false);
  const [flashOffer, setFlashOffer] = useState(false);

  // Cory "auto-nudge": a pickup tip sets the slider to 8% and flashes the launch button.
  React.useEffect(() => {
    if (!showSlider) return;
    const onNudge = () => {
      setShift(8);
      setFlashOffer(true);
      setTimeout(() => setFlashOffer(false), 2600);
    };
    window.addEventListener('qsr-pickup-nudge', onNudge);
    return () => window.removeEventListener('qsr-pickup-nudge', onNudge);
  }, [showSlider]);

  const deliverySales = sales * (mixPct / 100);
  const directSales = sales - deliverySales;
  const uberSales = deliverySales * 0.55;
  const ddSales = deliverySales * 0.45;

  // 12-week mix momentum — delivery share climbing ~0.7pt/wk (direct margin slipping).
  const mixTrend = Array.from({ length: 12 }, (_, i) => +(mixPct - (11 - i) * 0.7).toFixed(1));
  const mixStart = mixTrend[0];

  const rows = [
    { key: 'direct', name: 'Direct — dine-in & pickup', domain: null, tint: '#10b981', sales: directSales },
    { key: 'ubereats', name: 'Uber Eats', domain: 'ubereats.com', tint: '#06C167', sales: uberSales },
    { key: 'doordash', name: 'DoorDash', domain: 'doordash.com', tint: '#FF3008', sales: ddSales },
  ].map((r) => ({ ...r, profit: r.sales * (MARGINS[r.key] / 100) }));

  const totalProfit = rows.reduce((s, r) => s + r.profit, 0);
  const maxProfit = Math.max(...rows.map((r) => r.profit));
  const aggSalesShare = Math.round((deliverySales / sales) * 100);
  const aggProfitShare = Math.round((rows.filter((r) => r.key !== 'direct').reduce((s, r) => s + r.profit, 0) / totalProfit) * 100);

  // What-if: move `shift`% of delivery sales into the direct (pickup) channel.
  // Effective shift shown on the card: once the offer is live we freeze it at
  // (at least) 8% so the slider, gain and bars all agree with the projected state.
  const effShift = offerLive ? Math.max(shift, 8) : shift;
  const shifted = deliverySales * (effShift / 100);
  const remaining = deliverySales - shifted;
  const newProfit = (directSales + shifted) * (MARGINS.direct / 100)
    + remaining * 0.55 * (MARGINS.ubereats / 100)
    + remaining * 0.45 * (MARGINS.doordash / 100);
  const gain = newProfit - totalProfit;

  // Loyalty offer projection — the program targets ~2x the current slider's pickup shift
  // as adoption compounds over 12 months (min 16%), producing the headline annual win.
  const programShift = Math.min(50, Math.max(16, (shift || 8) * 2));
  const pgShifted = deliverySales * (programShift / 100);
  const pgRemaining = deliverySales - pgShifted;
  const pgProfit = (directSales + pgShifted) * (MARGINS.direct / 100)
    + pgRemaining * 0.55 * (MARGINS.ubereats / 100)
    + pgRemaining * 0.45 * (MARGINS.doordash / 100);
  const programAnnual = Math.round((pgProfit - totalProfit) * 52);

  // After the pickup shift (shown once the offer is live) — direct grows, aggregators shrink.
  const psShifted = deliverySales * (effShift / 100);
  const psRemaining = deliverySales - psShifted;
  const postRows = rows.map((r) => {
    const s = r.key === 'direct' ? directSales + psShifted : r.key === 'ubereats' ? psRemaining * 0.55 : psRemaining * 0.45;
    return { ...r, sales: s, profit: s * (MARGINS[r.key] / 100) };
  });
  const displayRows = offerLive ? postRows : rows;
  const displayMax = Math.max(...displayRows.map((r) => r.profit));

  return (
    <div className={`rounded-2xl qsr-card shadow-sm ${compact ? 'p-4' : 'p-4 sm:p-5'}`} data-testid="channel-profit">
      <div className="flex items-center justify-between mb-1 gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          Channel profit breakdown
          {offerLive && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 align-middle">projected after offer</span>}
        </h3>
        {subtitle && <span className="text-[11px] text-slate-400 text-right">{subtitle}</span>}
      </div>
      <p className="text-[11px] text-slate-500 mb-3">What each ordering channel actually contributes after aggregator commission and food cost.</p>

      <div className="space-y-3">
        {displayRows.map((c) => (
          <div key={c.key} data-testid={`channel-row-${c.key}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                {c.domain ? (
                  <img src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=64`} alt="" width={16} height={16} className="h-4 w-4 rounded object-contain shrink-0" />
                ) : (
                  <Store className="w-4 h-4 shrink-0" style={{ color: c.tint }} />
                )}
                <span className="text-sm font-medium text-slate-800 shrink-0">{c.name}</span>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0 whitespace-nowrap">
                  {COMMISSION[c.key] === 0 ? 'no commission' : `${COMMISSION[c.key]}% commission`}
                </span>
              </div>
              <div className="text-right shrink-0">
                <span className="text-sm font-bold" style={{ color: c.tint }}>{money(c.profit)}</span>
                <span className="text-[10px] text-slate-400 ml-1">net/wk</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${(c.profit / displayMax) * 100}%`, backgroundColor: c.tint }} />
              </div>
              <span className="text-[11px] text-slate-500 w-28 sm:w-32 text-right shrink-0 whitespace-nowrap">{money(c.sales)} · {MARGINS[c.key]}%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100" data-testid="channel-mix-trend">
        <div className="flex items-center justify-between mb-1.5 gap-2">
          <span className="text-xs font-medium text-slate-700">Delivery mix · 12 weeks</span>
          <span className="text-[11px] font-semibold text-amber-600">{mixStart.toFixed(0)}% → {mixPct.toFixed(0)}%</span>
        </div>
        <Sparkline data={mixTrend} color="#f59e0b" width={320} height={40} className="w-full" strokeWidth={2} />
        <p className="text-[11px] text-slate-500 mt-1.5">
          Delivery share is climbing — your high-margin <b className="text-slate-700">direct</b> share has slipped
          {' '}{(100 - mixStart).toFixed(0)}% → {(100 - mixPct).toFixed(0)}%. Momentum makes the pickup lever worth more every week.
        </p>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
        <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">Corvana AI:</span> Aggregators drive <b>{aggSalesShare}%</b> of sales but only <b>{aggProfitShare}%</b> of profit —
          after 28–30% commissions a delivery dollar is worth roughly half a direct one. Every order nudged to direct pickup is your highest-margin win.
        </p>
      </div>

      {showSlider && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid="channel-whatif">
          <div className="flex items-center justify-between mb-1.5 gap-2">
            <label className="text-xs font-medium text-slate-700">
              What-if: shift <b className="text-slate-900">{effShift}%</b> of delivery to direct pickup
            </label>
            <span className={`text-sm font-bold ${gain > 0 ? 'text-emerald-600' : 'text-slate-400'}`} data-testid="channel-whatif-gain">
              +{money(gain)}<span className="text-[10px] text-slate-400 font-medium">/wk</span>
            </span>
          </div>
          <input
            type="range" min="0" max="50" step="1" value={effShift}
            onChange={(e) => setShift(Number(e.target.value))}
            disabled={offerLive}
            className="w-full accent-emerald-500 cursor-pointer disabled:cursor-default disabled:opacity-70"
            data-testid="channel-pickup-slider"
            aria-label="Percent of delivery orders shifted to pickup"
          />
          <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
            <span>0%</span>
            <span className="text-emerald-600 font-semibold">{money(gain * 52)}/yr uplift</span>
            <span>50%</span>
          </div>

          <button
            data-testid="channel-loyalty-offer"
            disabled={offerLive}
            onClick={() => {
              setOfferLive(true);
              setFlashOffer(false);
              // Step the hero "Sales today / Orders today" up so the win lands live.
              const dailyWinSales = Math.round((deliverySales * (programShift / 100)) / 7);
              applyPayoff(dailyWinSales, Math.round(dailyWinSales / 18.6));
              const yr = `$${Math.round(programAnnual / 1000)}k`;
              toast.success(`Pickup loyalty offer launched — projected +${yr}/yr 🎉`, {
                description: `"2x points on pickup" pushed to the app, kiosk & receipts across all stores. Projected +${money(programAnnual)}/yr as pickup adoption ramps to ${Math.round(programShift)}%. (Demo)`,
                duration: 7000,
              });
            }}
            className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl text-sm font-semibold px-4 py-2.5 transition-colors ${
              offerLive ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 cursor-default' : 'bg-slate-900 text-white hover:bg-slate-800'
            } ${flashOffer && !offerLive ? 'pickup-flash' : ''}`}
          >
            {offerLive ? (<><Check className="w-4 h-4" /> Offer live — pushed to app, kiosk &amp; receipts</>) : (<><Gift className="w-4 h-4" /> Launch pickup loyalty offer</>)}
          </button>
          {offerLive && (
            <p className="mt-1.5 text-[11px] text-slate-500 text-center">
              Corvana is tracking pickup share daily — you'll get a nudge if it stalls.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
